import React, { useState, useEffect, useRef } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Loader from '../common/Loader.js';
import Button from '../common/Button.js';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import dqLogo from '../../static/images/logo_DQ.png';
import dqMobileLogo from '../../static/images/dq_logo.png';
import { isNativeApp } from '../../utils/nativeApp.js';
import LoginOtpModal from './LoginOtpModal.js';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [showCompanyPicker, setShowCompanyPicker] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpError, setOtpError] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpResending, setOtpResending] = useState(false);

  const navigate = useNavigate();
  const { login, verifyLoginOtp, resendLoginOtp, isAuthenticated, user, loading: authLoading } = useAuth();

  // ✅ FIX 1: Use a ref to prevent the redirect from firing more than once
  const hasRedirected = useRef(false);

  const redirectToDashboard = (role) => {
    // ✅ FIX 2: Guard so redirect only happens once per mount
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    console.log('🔀 [REDIRECT] Role received:', role);
    console.log('🔀 [REDIRECT] hasRedirected flag:', hasRedirected.current);

    switch (role) {
      case 'System Admin':
        navigate('/system-admin-dashboard', { replace: true });
        break;
      case 'Accountant':
        navigate('/accountant-dashboard', { replace: true });
        break;
      case 'Agent':
        navigate('/agent-dashboard', { replace: true });
        break;
      case 'Admin':
        navigate('/admin-dashboard', { replace: true });
        break;
      case 'Owner':
        navigate('/owner-dashboard', { replace: true });
        break;
      case 'Hub Admin':
        navigate('/admin-dashboard', { replace: true });
        break;
      case 'Contract Party Admin':
        navigate('/contract-party-dashboard', { replace: true });
        break;
      default:
        navigate('/admin-dashboard', { replace: true });
    }
  };

  // ✅ FIX 3: Only redirect when BOTH isAuthenticated AND user are stable/truthy
  // Also check loading is false so we don't redirect during an in-progress login
  useEffect(() => {
    console.log('👁️ [AUTH EFFECT] authLoading:', authLoading, '| isAuthenticated:', isAuthenticated, '| user:', user, '| loading:', loading);
    // Wait for auth context to finish initializing before redirecting
    if (!authLoading && isAuthenticated && user && user.role && !loading) {
      console.log('👁️ [AUTH EFFECT] All conditions met — redirecting for role:', user.role);
      redirectToDashboard(user.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user, loading]);

  // ✅ FIX 4: Reset the redirect guard when component unmounts (clean-up)
  useEffect(() => {
    return () => {
      hasRedirected.current = false;
    };
  }, []);

  useEffect(() => {
    const savedEmail = localStorage.getItem('savedEmail');
    const savedPassword = localStorage.getItem('savedPassword');
    const rememberMeStatus = localStorage.getItem('rememberMe') === 'true';

    if (rememberMeStatus && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🚀 [LOGIN] Form submitted');
    console.log('🚀 [LOGIN] Email entered:', email);
    console.log('🚀 [LOGIN] Password length:', password?.length);
    console.log('🚀 [LOGIN] Remember me:', rememberMe);

    try {
      if (rememberMe) {
        localStorage.setItem('savedEmail', email);
        localStorage.setItem('savedPassword', password);
        localStorage.setItem('rememberMe', 'true');
        console.log('💾 [LOGIN] Credentials saved to localStorage');
      } else {
        localStorage.removeItem('savedEmail');
        localStorage.removeItem('savedPassword');
        localStorage.removeItem('rememberMe');
        console.log('🗑️ [LOGIN] Saved credentials cleared from localStorage');
      }

      console.log('📡 [LOGIN] Sending request to /auth/login with email:', email);
      const response = await login(email, password, selectedCompanyId || null);
      console.log('✅ [LOGIN] Response received:', response);

      if (response?.otp_required) {
        setOtpChallenge(response);
        setOtpError('');
        hasRedirected.current = false;
        return;
      }

      console.log('✅ [LOGIN] User data:', response?.data);
      console.log('✅ [LOGIN] Role:', response?.data?.role);
      console.log('✅ [LOGIN] Token:', response?.data?.token);
      console.log('✅ [LOGIN] Token expiry:', response?.data?.token_expiry);
      console.log('✅ [LOGIN] Company:', response?.data?.company_name);
      console.log('✅ [LOGIN] sessionStorage after login:', sessionStorage.getItem('user'));

      if (response && response.data && response.data.role) {
        console.log('🔀 [LOGIN] Redirecting to dashboard for role:', response.data.role);
        redirectToDashboard(response.data.role);
      } else {
        console.warn('⚠️ [LOGIN] No role found in response — redirect skipped');
      }

    } catch (err) {
      console.error('❌ [LOGIN] Login failed');
      console.error('❌ [LOGIN] Error object:', err);
      console.error('❌ [LOGIN] HTTP status:', err?.response?.status);
      console.error('❌ [LOGIN] Server error message:', err?.response?.data?.error);
      console.error('❌ [LOGIN] Full server response:', err?.response?.data);

      if (err?.response?.status === 409 && err?.response?.data?.companies?.length) {
        setCompanyOptions(err.response.data.companies);
        setShowCompanyPicker(true);
        if (!selectedCompanyId && err.response.data.companies.length === 1) {
          setSelectedCompanyId(err.response.data.companies[0].company_id);
        }
        setError('Select your company to continue.');
        hasRedirected.current = false;
        return;
      }

      setError(err.response?.data?.error || 'Login failed. Please check your credentials and try again.');
      hasRedirected.current = false;
    } finally {
      setLoading(false);
      console.log('🏁 [LOGIN] Login attempt finished. Loading set to false.');
    }
  };

  const handleVerifyOtp = async (otp) => {
    if (!otpChallenge?.challenge_id) return;
    setOtpError('');
    setOtpVerifying(true);
    try {
      const response = await verifyLoginOtp(otpChallenge.challenge_id, otp);
      setOtpChallenge(null);
      if (response && response.data && response.data.role) {
        redirectToDashboard(response.data.role);
      }
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Invalid OTP. Please try again.');
      hasRedirected.current = false;
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpChallenge?.challenge_id) return;
    setOtpError('');
    setOtpResending(true);
    try {
      const response = await resendLoginOtp(otpChallenge.challenge_id);
      setOtpChallenge((prev) => ({
        ...(prev || {}),
        ...response,
        challenge_id: response.challenge_id || prev?.challenge_id,
      }));
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Failed to resend OTP. Please try again.');
    } finally {
      setOtpResending(false);
    }
  };

  return (
    <div className="min-h-screen md:h-screen relative md:overflow-hidden bg-transparent">

      {/* ===== BACKGROUND IMAGE (tablet & desktop only) ===== */}
      <div
        className="absolute inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: "url('/images/login-bg-gradient.png')",
        }}
      />

      {/* ===== CONTENT WRAPPER ===== */}
      <div className="relative z-10 min-h-screen md:h-full flex items-center justify-center md:justify-start py-10 md:py-0">
        <div className="container mx-auto px-4 md:px-6 w-full">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

            {/* ================= LEFT CONTENT (tablet & desktop only) ================= */}
            <div className="block">

              <h1 className="text-4xl font-bold tracking-tight">
                {isNativeApp() ? (
                  <img
                    src={dqMobileLogo}
                    alt="DQ Logix Logistics"
                    className="h-16 sm:h-20 w-auto object-contain"
                  />
                ) : (
                  <>
                    <img
                      src={dqMobileLogo}
                      alt="DQ Logix Logistics"
                      className="h-16 w-auto object-contain md:hidden"
                    />
                    <img
                      src={dqLogo}
                      alt="DQ Logix Logistics"
                      className="w-[30%] hidden md:block"
                    />
                  </>
                )}
              </h1>

              <p className="text-lg uppercase tracking-widest text-gray-500 mt-6">
                From shipment to settlement,
              </p>

              <p className="text-3xl font-semibold text-[#3467ae] uppercase mt-2">
                We support your entire journey.
              </p>

              <img
                src="/images/dq-logix-login-illustration.png"
                alt="DQ Logix Logistics"
                className="w-full max-w-4xl drop-shadow-xl mt-10 hidden md:block"
              />

              <p className="text-sm text-gray-400 text-left mt-8 hidden md:block">
                All Rights Reserved. Designed with passion by{" "}
                <span className="font-semibold text-[#3467ae]">DIGIQIN</span>
              </p>
            </div>

            {/* ================= RIGHT LOGIN CARD ================= */}
            <div className="flex justify-center md:justify-end w-full">
              <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-8">

                <div>
                  <h2 className="text-xl font-semibold mb-4">
                    FOR PARTNERS
                  </h2>

                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="email"
                      placeholder="EMAIL"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="login-input w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none"
                      required
                      disabled={loading}
                    />

                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="PASSWORD"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="login-input w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none pr-12"
                        required
                        disabled={loading}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={showPassword ? EyeOff : Eye}
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 !p-1 !border-0"
                      />
                    </div>

                    {showCompanyPicker && companyOptions.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 uppercase">
                          Select Company
                        </label>
                        <SelectField variant="form"
                          value={selectedCompanyId}
                          onChange={(e) => setSelectedCompanyId(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                          required
                          disabled={loading}
                        >
                          <option value="">Choose company...</option>
                          {companyOptions.map((option) => (
                            <option key={option.company_id} value={option.company_id}>
                              {option.company_name} ({option.company_id}) — {option.role}
                            </option>
                          ))}
                        </SelectField>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          disabled={loading}
                        />
                        Remember me
                      </label>

                      <Link
                        to="/forgot-password"
                        className="hover:text-indigo-700 transition"
                      >
                        Forget password
                      </Link>
                    </div>

                    <Button
                      variant="primary"
                      type="submit"
                      fullWidth
                      loading={loading}
                      size="lg"
                      className="py-3 rounded-lg"
                    >
                      {loading ? 'SIGNING IN...' : 'SIGN IN'}
                    </Button>

                    <div className="pt-2">
                      <Link
                        to="/"
                        className="inline-flex items-center justify-center w-full py-2.5 px-4 rounded-lg border border-[#3467ae] text-sm font-medium text-[#3467ae] hover:bg-[#3467ae]/5 transition"
                      >
                        ← Back to Home
                      </Link>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      By clicking on Sign In, I accept the Terms & Conditions
                      and Privacy Policy.
                    </p>
                  </form>
                </div>

              </div>
            </div>

            <p className="text-sm text-gray-400 text-left mt-0 md:hidden sm:block">
                All Rights Reserved. Designed with passion by{" "}
                <span className="font-semibold text-[#3467ae]">DIGIQIN</span>
            </p>

          </div>

        </div>
      </div>
      <LoginOtpModal
        open={Boolean(otpChallenge)}
        challenge={otpChallenge}
        verifying={otpVerifying}
        resending={otpResending}
        error={otpError}
        onClose={() => {
          if (otpVerifying) return;
          setOtpChallenge(null);
          setOtpError('');
        }}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
      />
    </div>
  );
};

export default Login;